import { useMemo } from "react";
import { RotateCcw } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useFilters } from "@/context/FilterContext";
import { BULAN } from "@/lib/format";

const ALL = "__all";
const MODES = [{ value: "tahun", label: "Tahunan" }, { value: "semester", label: "Semester" }, { value: "triwulan", label: "Triwulan" }, { value: "bulan", label: "Bulanan" }];
const PERIOD_OPTS = {
  semester: [1, 2].map((v) => ({ value: String(v), label: `Semester ${v}` })),
  triwulan: ["I", "II", "III", "IV"].map((l, i) => ({ value: String(i + 1), label: `Triwulan ${l}` })),
  bulan: BULAN.map((b, i) => ({ value: String(i + 1), label: b })),
};

function FSelect({ testid, label, value, onChange, options, allLabel, disabled }) {
  return (
    <div className="flex min-w-[8.5rem] shrink-0 flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      <Select value={value ?? ALL} onValueChange={(v) => onChange(v === ALL ? null : v)} disabled={disabled}>
        <SelectTrigger data-testid={testid} className="h-9 bg-background text-sm"><SelectValue /></SelectTrigger>
        <SelectContent>
          {allLabel && <SelectItem value={ALL}>{allLabel}</SelectItem>}
          {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

export const FilterBar = () => {
  const { filters, setFilters, resetFilters, meta } = useFilters();
  const units = useMemo(() => {
    const list = (meta?.units || []).filter((u) => (!filters.uid || u.uid === filters.uid) && (!filters.tipe || u.tipe === filters.tipe));
    return [...new Set(list.map((u) => u.unit))].map((u) => ({ value: u, label: u }));
  }, [meta, filters.uid, filters.tipe]);
  if (!meta) return null;
  const opt = (arr) => arr.map((v) => ({ value: String(v), label: String(v) }));

  return (
    <div data-testid="filter-bar" className="sticky top-14 z-30 border-b bg-background/95 backdrop-blur-md">
      <div className="flex items-end gap-3 overflow-x-auto px-4 py-3 sm:px-6 lg:px-8">
        <FSelect testid="filter-uid-select" label="UID" value={filters.uid} onChange={(v) => setFilters({ uid: v })} options={opt(meta.uids)} allLabel="Semua UID" />
        <FSelect testid="filter-tipe-select" label="Tipe Project" value={filters.tipe} onChange={(v) => setFilters({ tipe: v })} options={opt(meta.tipes)} allLabel="Semua Tipe" />
        <FSelect testid="filter-unit-select" label="Unit / UP3" value={filters.unit} onChange={(v) => setFilters({ unit: v })} options={units} allLabel={meta.scoped ? "Semua unit saya" : "Semua Unit"} />
        <FSelect testid="filter-tahun-select" label="Tahun" value={filters.tahun ? String(filters.tahun) : null} onChange={(v) => setFilters({ tahun: Number(v) })} options={opt(meta.years)} />
        <FSelect testid="filter-mode-select" label="Periode" value={filters.mode} onChange={(v) => setFilters({ mode: v })} options={MODES} />
        {filters.mode !== "tahun" && (
          <FSelect testid="filter-value-select" label="Pilih" value={filters.value ? String(filters.value) : null} onChange={(v) => setFilters({ value: Number(v) })} options={PERIOD_OPTS[filters.mode]} />
        )}
        <Button data-testid="filter-reset-button" variant="ghost" size="sm" className="h-9 shrink-0" onClick={resetFilters}>
          <RotateCcw className="mr-1.5 h-3.5 w-3.5" />Reset
        </Button>
      </div>
    </div>
  );
};
